import React from "react";
import { Landmark, Mail, MapPin, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Footer({ onCtaClick }) {
  return (
    <footer
      className="bg-slate-950 text-slate-300"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                <Landmark className="h-5 w-5" />
              </div>
              <span className="font-extrabold text-xl text-white tracking-tight">
                Bancoorp
              </span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-md">
              Bancoorp es un <strong className="text-slate-200">intermediario de crédito</strong>.
              No somos banco ni una institución financiera regulada, y{" "}
              <strong className="text-slate-200">
                no otorgamos directamente los créditos
              </strong>
              . La aprobación y las condiciones finales del crédito dependen de
              la entidad financiera.
            </p>

            <div className="mt-6 space-y-3 text-sm">
              <div className="flex items-start gap-3" data-testid="footer-address">
                <MapPin className="h-4 w-4 mt-0.5 text-teal-400 shrink-0" />
                <span>
                  Av. Apoquindo 1234, Piso 8, Oficina 802
                  <br />
                  Las Condes, Santiago, Chile
                </span>
              </div>
              <div className="flex items-center gap-3" data-testid="footer-phone">
                <MessageCircle className="h-4 w-4 text-teal-400 shrink-0" />
                <button
                  type="button"
                  onClick={onCtaClick}
                  className="hover:text-white transition-colors text-left"
                >
                  Contactar por WhatsApp
                </button>
              </div>
              <div className="flex items-center gap-3" data-testid="footer-email">
                <Mail className="h-4 w-4 text-teal-400 shrink-0" />
                <a
                  href="mailto:contacto@bancoorp.cl"
                  className="hover:text-white transition-colors"
                >
                  contacto@bancoorp.cl
                </a>
              </div>
            </div>
            <div className="mt-7">
              <Button
                onClick={onCtaClick}
                className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-full px-6 py-5"
                data-testid="footer-whatsapp-cta"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Contactar por WhatsApp
              </Button>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">
              Empresa
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a href="/" className="hover:text-white">Inicio</a></li>
              <li><a href="/servicios" className="hover:text-white">Servicios</a></li>
              <li><a href="/como-funciona" className="hover:text-white">Cómo funciona</a></li>
              <li><a href="/empresas" className="hover:text-white">Empresas</a></li>
              <li><a href="/nosotros" className="hover:text-white">Nosotros</a></li>
              <li><a href="/evaluacion" className="hover:text-white">Evaluación</a></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">
              Legal
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><a href="#legal" className="hover:text-white">Aviso legal</a></li>
              <li><a href="#terminos" className="hover:text-white">Términos y condiciones</a></li>
              <li><a href="#privacidad" className="hover:text-white">Política de privacidad</a></li>
              <li><a href="#cookies" className="hover:text-white">Política de cookies</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">
              Recursos oficiales
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a
                  href="https://www.cmfchile.cl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  CMF · Comisión para el Mercado Financiero
                </a>
              </li>
              <li>
                <a
                  href="https://www.sernac.cl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  SERNAC · Servicio Nacional del Consumidor
                </a>
              </li>
              <li>
                <a
                  href="https://www.bcentral.cl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  Banco Central de Chile
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div
          id="legal"
          className="mt-12 pt-8 border-t border-slate-800 text-xs text-slate-500 leading-relaxed space-y-3"
          data-testid="footer-legal-text"
        >
          <p>
            <strong className="text-slate-400">Aviso legal:</strong> Bancoorp SpA
            (RUT 76.XXX.XXX-X) opera como intermediario de crédito en Chile,
            conectando solicitantes con entidades financieras aliadas. Bancoorp{" "}
            <strong className="text-slate-300">no es banco, emisor de tarjetas, cooperativa ni caja de compensación</strong>,
            y no está sujeto directamente a la fiscalización de la Comisión para
            el Mercado Financiero (CMF). Las entidades financieras con las que
            trabajamos sí operan bajo la normativa vigente y son responsables de
            la evaluación, aprobación, contratación y cobranza del crédito.
          </p>
          <p>
            <strong className="text-slate-400">Condiciones referenciales:</strong>{" "}
            Las tasas indicadas en este sitio (desde 1% mensual) y los ejemplos
            de crédito (incluido el ejemplo de $1.000.000 CLP a 12 meses con CAE
            estimada) son meramente referenciales y no constituyen una oferta.
            La tasa, CAE, cuota, costo total, plazo y demás condiciones serán
            informadas por la entidad financiera en la oferta vinculante, previa
            evaluación crediticia. Plazo mínimo legal de operaciones de crédito:
            61 días.
          </p>
          <p>
            <strong className="text-slate-400">Información al consumidor:</strong>{" "}
            Antes de contratar, revisa cuidadosamente las condiciones,
            especialmente la CAE (Carga Anual Equivalente), los seguros
            asociados y el costo total del crédito. El no pago oportuno genera
            intereses moratorios, gastos de cobranza y afecta el historial
            crediticio. Ante dudas o reclamos, puedes contactar a la entidad
            financiera, al SERNAC o a la CMF.
          </p>
          <p>
            <strong className="text-slate-400">Protección de datos:</strong> El
            tratamiento de datos personales se rige por la Ley N° 19.628. Puedes
            ejercer tus derechos escribiendo a{" "}
            <a
              href="mailto:privacidad@bancoorp.cl"
              className="text-teal-400 hover:underline"
            >
              privacidad@bancoorp.cl
            </a>
            .
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <span>
            © {new Date().getFullYear()} Bancoorp SpA. Todos los derechos reservados.
          </span>
          <span>
            Bancoorp actúa como intermediario y no otorga directamente los
            créditos.
          </span>
        </div>
      </div>
    </footer>
  );
}
